import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, ShoppingCart, User, Menu, X, ChevronDown, Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MegaMenu } from "@/components/MegaMenu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import type { Category } from "@/data/categories";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { cn } from "@/lib/utils";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useTranslation } from "react-i18next";

export const Header = () => {
  const { t } = useTranslation('common');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const navigate = useNavigate();
  const handleScrollToTop = useScrollToTop();

  useEffect(() => {
    const fetchCategories = async () => {
      const { data } = await supabase
        .from("categories")
        .select("*")
        .order("name");
      
      if (data) {
        setCategories(data.map((cat: any) => ({
          id: cat.id,
          name: cat.name,
          slug: cat.slug,
          subcategories: Array.isArray(cat.subcategories) ? cat.subcategories : []
        })));
      }
    };
    
    fetchCategories();
  }, []);
  const { user, signOut } = useAuth();
  const { count } = useCart();
  const { wishlistItems } = useWishlist();
  const wishlistCount = wishlistItems.length;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    handleScrollToTop(`/search-results?q=${encodeURIComponent(q)}`);
    setIsMenuOpen(false);
    setIsCategoriesOpen(false);
  };

  const navigationItems = [
    { label: "Home", path: "/" },
    { label: "New Arrivals", path: "/new-arrivals" },
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-backdrop-blur border-b border-border hidden md:block">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div 
            className="font-display font-bold text-2xl text-primary cursor-pointer"
            onClick={() => handleScrollToTop("/")}
          >
            PERLLA
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button
              className="inline-flex items-center gap-1 hover:text-primary perlla-transition"
              onClick={() => setIsCategoriesOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={isCategoriesOpen}
            >
              {t('navigation.categories')}
              <ChevronDown className={cn(
                "w-4 h-4 perlla-transition transform",
                isCategoriesOpen ? "rotate-0" : "rotate-180"
              )} />
            </button>
            <button
              onClick={() => handleScrollToTop('/new-arrivals')}
              className="text-foreground hover:text-primary perlla-transition"
            >
              {t('navigation.newArrivals')}
            </button>
          </nav>
          {/* Desktop Mega Menu */}
          <MegaMenu isOpen={isCategoriesOpen} onClose={() => setIsCategoriesOpen(false)} />

          {/* Search Bar - Desktop */}
          <div className="hidden lg:flex items-center max-w-sm">
            <form className="relative w-full" onSubmit={handleSearch}>
              <button
                type="submit"
                aria-label="Search"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-6 h-6 flex items-center justify-center"
              >
                <Search className="h-5 w-5" />
              </button>
              <Input
                placeholder={t('header.searchPlaceholder')}
                className="pl-10 bg-muted/50"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </form>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              className="hidden lg:flex relative"
              onClick={() => handleScrollToTop("/account/wishlist")}
              aria-label={t('header.wishlist')}
            >
              <Heart 
                className={cn(
                  "h-5 w-5 transition-colors",
                  wishlistCount > 0 && "fill-primary text-primary"
                )} 
              />
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </Button>
            
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => handleScrollToTop("/cart")}
              className="relative"
              aria-label={t('header.cart')}
            >
              <ShoppingCart className="h-5 w-5" />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {count}
                </span>
              )}
            </Button>
            
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => handleScrollToTop(user ? "/account" : "/auth")}
              aria-label={user ? t('header.account') : t('header.signIn')}
            >
              <User className="h-5 w-5" />
            </Button>
            {user && (
              <Button variant="ghost" size="sm" onClick={signOut} className="hidden lg:inline-flex">
                {t('header.signOut')}
              </Button>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setIsMenuOpen(true)}
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <SheetContent side="left" className="w-[85%] sm:max-w-sm">
            <SheetHeader>
              <SheetTitle>{t('navigation.categories')}</SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-6">
              {/* Mobile Search */}
              <form className="relative" onSubmit={handleSearch}>
                <button
                  type="submit"
                  aria-label="Search"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-6 h-6 flex items-center justify-center"
                >
                  <Search className="h-5 w-5" />
                </button>
                <Input
                  placeholder={t('header.searchPlaceholder')}
                  className="pl-10 bg-muted/50"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </form>

              {/* Categories Accordion */}
              <Accordion type="single" collapsible>
                <AccordionItem value="categories">
                  <AccordionTrigger>{t('navigation.categories')}</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2">
                      {categories.map((cat) => (
                        <button
                          key={cat.id}
                          className="block w-full text-left py-2 text-foreground hover:text-primary perlla-transition"
                          onClick={() => {
                            handleScrollToTop(`/category/${cat.slug}`)
                            setIsMenuOpen(false)
                          }}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Simple Links */}
              <nav className="flex flex-col space-y-4">
                <button
                  onClick={() => { handleScrollToTop('/new-arrivals'); setIsMenuOpen(false); }}
                  className="text-left text-foreground hover:text-primary perlla-transition font-medium"
                >
                  {t('navigation.newArrivals')}
                </button>
              </nav>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};